import type { AuditInfo, EntityMetadata } from "../common";
import type { TrainingData } from "../data-source";
import type {
  IDescriptor,
  IFormulationDescriptor,
  IInorganicChemicalFormulaDescriptor,
  IOrganicChemicalFormulaDescriptor,
  IRealDescriptor,
  IValueDescriptor,
} from "../descriptor/descriptor";
import type { ExplicitTypeMap } from "../utils";

export interface PredictorData {
  description: string;
  instance: IGraphMLPredictorConfig;
  name: string;
}

export interface PredictorMetadata extends EntityMetadata {
  draft: boolean;
  version: number;
  root_archived?: AuditInfo;
}

export interface PredictorEntity {
  id: string;
  data: PredictorData;
  metadata: PredictorMetadata;
}

// these are based on
// https://github.com/CitrineInformatics/platform-backend/blob/b7e39197ee0816f1d8422dde499322a3ead57441/code/orion/ml-config-core/src/main/scala/io/citrine/orion/core/mithril/predictors/OrionPredictorConfiguration.scala
interface IBasePredictorConfig {
  name: string;
  description: string;
}

/** https://citrineinformatics.github.io/citrine-python/reference/citrine.informatics.predictors.auto_ml_predictor.html#citrine.informatics.predictors.auto_ml_predictor.AutoMLEstimator */
export const AUTO_ML_ESTIMATORS = [
  "RANDOM_FOREST", // valid for single-task and multi-task regression and classification problems
  "LINEAR", // valid for single-task regression problems
  "GAUSSIAN_PROCESS", // valid for single-task regression and classification problems
  "SUPPORT_VECTOR_MACHINE", // valid for single-task classification problems
] as const;
export type AutoMLEstimator = (typeof AUTO_ML_ESTIMATORS)[number];

export interface IAutoMLPredictorConfig extends IBasePredictorConfig {
  type: "AutoML";
  estimators?: AutoMLEstimator[] | readonly ["ALL"];
  inputs: IValueDescriptor[];
  outputs: IValueDescriptor[];
}

export interface ISimpleMLPredictorConfig extends IBasePredictorConfig {
  type: "Simple";
  inputs: IDescriptor[];
  outputs: IDescriptor[];
  latent_variables: IDescriptor[];
}

// Deprecated in favor of Analytic Expression
export interface IExpressionMLPredictorConfig extends IBasePredictorConfig {
  type: "Expression";
  expression: string;
  output: IRealDescriptor;
  aliases: Partial<Record<string, string>>;
}

export interface IAnalyticExpressionPredictorConfig extends IBasePredictorConfig {
  type: "AnalyticExpression";
  expression: string;
  output: IRealDescriptor;
  aliases: Partial<Record<string, IDescriptor>>;
}

export interface IGraphMLPredictorConfig<
  DataSource extends TrainingData = TrainingData,
> extends IBasePredictorConfig {
  type?: "Graph";
  predictors: ScalarPredictorConfig[];
  uid: string;
  training_data: DataSource[] | null;
}
export interface IIngredientsToSimpleMixturePredictorConfig extends IBasePredictorConfig {
  type: "IngredientsToSimpleMixture";
  output: IFormulationDescriptor;
  id_to_quantity: Partial<Record<string, IRealDescriptor>>;
  labels: Partial<Record<string, string[]>>;
}

export interface IGeneralizedMeanPropertyPredictorConfig extends IBasePredictorConfig {
  type: "GeneralizedMeanProperty";
  input: IFormulationDescriptor;
  properties: string[];
  p: number;
  impute_properties: boolean;
  default_properties: Partial<Record<string, number>> | null;
  label: string | null;
}

/**
 * Apparent replacement for the GeneralizedMeanPropertyPredictor - the primary
 * difference is that properties is a list of Descriptors instead of list of strings
 */
export interface IMeanPropertyPredictorConfig extends Omit<
  IGeneralizedMeanPropertyPredictorConfig,
  "type" | "properties"
> {
  type: "MeanProperty";
  properties: IDescriptor[];
  s3Key?: string;
}

export interface ISimpleMixturePredictorConfig extends IBasePredictorConfig {
  type: "SimpleMixture";
  input: IFormulationDescriptor;
  output: IFormulationDescriptor;
}

export interface ILabelFractionsPredictorConfig extends IBasePredictorConfig {
  type: "LabelFractions";
  input: IFormulationDescriptor;
  labels: string[];
}

export interface IIngredientFractionsPredictorConfig extends IBasePredictorConfig {
  type: "IngredientFractions";
  input: IFormulationDescriptor;
  ingredients: string[];
}

export interface IMoleculeFeaturizerPredictorConfig extends IBasePredictorConfig {
  type: "MoleculeFeaturizer";
  // Note `descriptor will change to `input` in the future
  descriptor: IOrganicChemicalFormulaDescriptor;
  excludes?: string[];
  features: string[];
}

export interface IChemicalFormulaFeaturizerPredictorConfig extends IBasePredictorConfig {
  type: "ChemicalFormulaFeaturizer";
  input: IInorganicChemicalFormulaDescriptor;
  excludes?: string[];
  features: string[];
  powers: number[];
}

export interface AttributeAccumulationConfig extends IBasePredictorConfig {
  type: "AttributeAccumulation";
  attributes: IValueDescriptor[];
  sequential: boolean;
}

export type ScalarPredictorConfig =
  | IAutoMLPredictorConfig
  | ISimpleMLPredictorConfig
  | IExpressionMLPredictorConfig
  | IAnalyticExpressionPredictorConfig
  | IGeneralizedMeanPropertyPredictorConfig
  | IIngredientsToSimpleMixturePredictorConfig
  | IMeanPropertyPredictorConfig
  | ISimpleMixturePredictorConfig
  | ILabelFractionsPredictorConfig
  | IIngredientFractionsPredictorConfig
  | IMoleculeFeaturizerPredictorConfig
  | IChemicalFormulaFeaturizerPredictorConfig
  | AttributeAccumulationConfig;

export type IPredictorConfig = ScalarPredictorConfig | IGraphMLPredictorConfig;

export type PredictorConfigMap = ExplicitTypeMap<ScalarPredictorConfig>;
